// Atlas orchestration (ATL-1/2/6/7, ELV-1, SOL-1). Client-side. Resolves a location
// request into everything the sim needs for an Atlas world — the intermediate city,
// the real-elevation payload, and the geographic centre that drives real solar
// position. The default KHFD location parses the BUNDLED fixtures with zero network
// (offline-first); any other location geocodes ONCE (ATL-1/7) then queries Overpass +
// SRTM at that centre; ANY failure falls back to the deterministic synthesizer (ATL-2).
import { ATLAS } from '../../sim/config';
import type { IntermediateCity } from '../../sim/atlas/intermediate';
import type { AtlasElevation, DemTile } from '../../sim/atlas/elevation';
import { synthesizeCity } from '../../sim/atlas/fallback';
import { parseOverpass, fetchOverpass, type OverpassJson, type LatLon } from './overpass';
import { fetchDemTile } from './dem';
import { geocode, bboxAround } from './geocode';
// The committed KHFD fixtures, bundled so the default location renders offline with
// real relief and zero network (ATL-6 / ELV-1).
import khfdFixture from '../../../e2e/fixtures/atlas-khfd.json';
import demKhfdFixture from '../../../e2e/fixtures/dem-khfd.json';

export { parseAtlasParam, type AtlasDeepLink } from './deeplink';

export interface AtlasRequest {
  /** Free-text location; empty/undefined + no lat/lon ⇒ the default KHFD (ATL-6). */
  location?: string;
  lat?: number;
  lon?: number;
  scale: number;
}

/** Everything an Atlas world needs from the network/fixture layer (ATL-1). */
export interface ResolvedAtlas {
  city: IntermediateCity;
  elevation: AtlasElevation;
  /** The world's geographic centre — drives real solar position (SOL-1). Null only
   *  when a location neither geocoded nor came with explicit coords (a pure ATL-2
   *  fallback), in which case the sky stays on the generic cycle. */
  center: LatLon | null;
}

const KHFD: LatLon = { lat: ATLAS.DEFAULT.lat, lon: ATLAS.DEFAULT.lon };

function sizeFor(scale: number): number {
  return Math.max(140, Math.round(ATLAS.BBOX_M / scale / 2));
}

function isDefault(req: AtlasRequest): boolean {
  return req.lat === undefined && (!req.location || req.location.trim() === '' || req.location.trim() === ATLAS.DEFAULT.label);
}

/**
 * Resolve an Atlas request into a city + elevation + centre, offline-first with an
 * ATL-2 fallback. Geocodes at most once (ATL-1 politeness), and pulls the SRTM tile
 * at the same resolved centre so a geocoded address gets real relief too (ELV-1).
 */
export async function resolveAtlas(req: AtlasRequest): Promise<ResolvedAtlas> {
  const scale = req.scale, size = sizeFor(scale);

  if (isDefault(req)) {
    const city = parseOverpass(khfdFixture as OverpassJson, KHFD, scale, size, ATLAS.DEFAULT.label);
    return { city, elevation: { dem: demKhfdFixture as DemTile, center: KHFD, scale }, center: KHFD };
  }

  const explicit = req.lat !== undefined && req.lon !== undefined;
  const label = req.location ?? `${req.lat},${req.lon}`;
  try {
    const center: LatLon = explicit ? { lat: req.lat!, lon: req.lon! } : await geocode(req.location!);
    const [s, w, n, e] = bboxAround(center, ATLAS.BBOX_M);
    const json = await fetchOverpass(s, w, n, e);
    const city = parseOverpass(json, center, scale, size, label);
    if (city.roads.length + city.buildings.length + city.green.length === 0) throw new Error('empty result');
    const dem = await fetchDemTile(center, ATLAS.BBOX_M); // geocoded-location relief (M31.2 / ELV-1)
    return { city, elevation: { dem, center, scale }, center };
  } catch (err) {
    console.warn('[atlas] falling back to a simulated city (ATL-2):', err);
    // Keep a real centre for solar when the user gave explicit coords; a pure
    // fallback (failed geocode) has no real location → generic sky.
    const center: LatLon | null = explicit ? { lat: req.lat!, lon: req.lon! } : null;
    return { city: synthesizeCity(label, scale), elevation: { dem: null, center: center ?? KHFD, scale }, center };
  }
}
